/**
 * The portal's front page: what is waiting for this client, and where things
 * stand.
 *
 * It replaces a row of five number tiles. Those answered "how many", which for
 * most clients is a row of zeros, and left "what do I do now" to be worked out
 * by opening each tab in turn. This page answers the second question first: the
 * invoice to pay, the quote to answer and the contract to sign are named, each
 * with its amount, its deadline and the button that deals with it.
 *
 * ── IT OWNS NO DATA OF ITS OWN ─────────────────────────────────────────────
 *
 * Everything here is read from the same lists the tabs behind it serve, and
 * only for the modules this contact was granted (`summary.modules`). A closed
 * module is not asked for at all, so this page cannot show a client something
 * the tab bar would not let them open. That also makes it safe in a staff
 * preview: every read goes through `portal.*`, which carries the preview token,
 * so what an operator sees here is the client's page and never their own.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2, Inbox, RefreshCw } from "lucide-react";
import { Badge, Button, Card, Skeleton } from "@wa/ui";
import type { PortalModule } from "@wa/core/portal-modules";
import { useT } from "../../../i18n/I18nProvider";
import type { AccountUser } from "../../../lib/auth";
import {
  portal,
  type PortalContract, type PortalEstimate, type PortalInvoice, type PortalProject,
  type PortalSummary, type PortalTicket,
} from "../../../lib/portal";
import { keepPreview } from "../../../lib/previewMode";
import { useShell } from "../../shell/context";
import { EmptyState, ListSkeleton } from "../../ui";
import { invoiceTotals } from "../invoices/lib";
import { buildAttention, type AttentionItem } from "./attention";
import { AttentionList } from "./AttentionList";
import { ActiveProjects, BalanceCard, BalanceRow, SupportCard } from "./OverviewCards";
import { usePayable } from "./usePayable";

interface Lists {
  invoices?: PortalInvoice[];
  estimates?: PortalEstimate[];
  contracts?: PortalContract[];
  projects?: PortalProject[];
  tickets?: PortalTicket[];
  /** At least one list this contact may read did not answer. */
  partial: boolean;
}

/** A project that is still moving, by the status the operator set. */
const ACTIVE_PROJECT = new Set(["not_started", "in_progress", "on_hold"]);

/** How many rows the two secondary lists show before "all" takes over. */
const PREVIEW_ROWS = 3;

/** How many card-payment probes the front page makes. The rows past these
 *  still open their invoice; they just do not get the one-click shortcut. */
const PAYABLE_PROBES = 3;

export function PortalOverview({
  user,
  summary,
  preview,
}: {
  /** Null in a staff preview: the page then greets nobody and names the client. */
  user: AccountUser | null;
  summary: PortalSummary | null;
  preview: boolean;
}) {
  const t = useT();
  const { variant } = useShell();
  const [, setParams] = useSearchParams();
  const [lists, setLists] = useState<Lists | null>(null);
  // Bumped by "Try again": the lists are read once per value.
  const [attempt, setAttempt] = useState(0);

  const modules = summary?.modules ?? [];
  const moduleKey = modules.join(",");
  const shows = (m: PortalModule) => modules.includes(m);

  useEffect(() => {
    if (!summary) return;
    let live = true;
    let partial = false;
    setLists(null);
    // A list is read only when its module is open, and one that fails marks
    // the page as partial rather than passing for empty: "nothing is waiting
    // for you" is a claim this page may not make about a list it never got.
    const read = <T,>(module: PortalModule, load: () => Promise<T>): Promise<T | undefined> =>
      modules.includes(module)
        ? load().catch(() => {
            partial = true;
            return undefined;
          })
        : Promise.resolve(undefined);

    void Promise.all([
      read("invoices", () => portal.invoices()),
      read("estimates", () => portal.estimates()),
      read("contracts", () => portal.contracts()),
      read("projects", () => portal.projects()),
      read("support", () => portal.tickets()),
    ]).then(([invoices, estimates, contracts, projects, tickets]) => {
      if (live) setLists({ invoices, estimates, contracts, projects, tickets, partial });
    });
    return () => {
      live = false;
    };
    // `moduleKey` stands for `modules`: the same grants in a new array must not
    // refetch five lists.
  }, [summary, moduleKey, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const attention = useMemo(() => (lists ? buildAttention(lists) : []), [lists]);
  const totals = useMemo(() => (lists?.invoices ? invoiceTotals(lists.invoices) : null), [lists]);
  const activeProjects = (lists?.projects ?? []).filter((p) => ACTIVE_PROJECT.has(p.status));
  const tickets = (lists?.tickets ?? []).slice(0, PREVIEW_ROWS);

  // A preview is read-only - the API refuses every write under it - so a card
  // button there could only lead to an error. Those rows open the invoice.
  const payableIds = attention
    .filter((item) => item.kind === "invoice")
    .slice(0, PAYABLE_PROBES)
    .map((item) => item.id);
  const { payable, busyId, failed: payFailed, pay } = usePayable(payableIds, !preview);

  const go = (query: Record<string, string>) => setParams(keepPreview(query));
  function open(item: AttentionItem) {
    if (item.kind === "invoice") go({ tab: "invoices", doc: item.id });
    else if (item.kind === "estimate") go({ tab: "estimates", doc: item.id });
    else go({ tab: "contracts", doc: item.id });
  }

  const clients = summary?.clients ?? [];
  const companies = clients.map((c) => c.name).join(" · ");
  // The person, or in a preview the company whose page this is. Never the
  // operator's own name over a client's records.
  const title = user
    ? t("app.greeting", { name: user.name || user.email })
    : clients[0]?.name ?? t("tabs.overview");
  const Title = variant === "portal" ? "h1" : "h2";

  const loading = !summary || !lists;
  const nothingOpen = !!summary && modules.length === 0;
  const hasTodoModule = shows("invoices") || shows("estimates") || shows("contracts");
  // A list failed and nothing was found in the ones that answered. The page
  // cannot say "nothing is waiting": the thing that waits may be in the list
  // it never got. It says what happened instead, and offers to read again.
  const unsure = !!lists?.partial && attention.length === 0;
  const lead = loading || unsure
    ? null
    : attention.length === 0
      ? t("portal.overview.leadNone")
      : attention.length === 1
        ? t("portal.overview.leadOne")
        : t("portal.overview.leadMany", { count: attention.length });

  return (
    <div>
      <div className="mb-8">
        {user && companies && (
          <p className="text-xs font-semibold uppercase tracking-wider text-foreground/70">{companies}</p>
        )}
        <Title className="mt-1 text-3xl font-extrabold tracking-tight text-balance md:text-4xl">{title}</Title>
        {!nothingOpen && hasTodoModule && (
          <div className="mt-2 min-h-6 max-w-prose text-pretty text-foreground/70">
            {lead ?? (loading && <Skeleton className="h-5 w-72 max-w-full" />)}
          </div>
        )}
      </div>

      {nothingOpen ? (
        <Card>
          <EmptyState icon={Inbox} title={t("portal.overview.emptyTitle")} body={t("portal.overview.emptyBody")} />
        </Card>
      ) : (
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="min-w-0 space-y-10">
            {/* Some lists answered and some did not: what is shown is true, it
                is just not everything. */}
            {lists?.partial && !unsure && (
              <p role="status" className="rounded-xl border border-border bg-muted/50 px-4 py-3 text-sm">
                {t("portal.overview.partial")}
              </p>
            )}

            {shows("invoices") && summary && (
              <BalanceRow
                outstandingCents={summary.stats.outstandingCents}
                currency={totals?.currency ?? "EUR"}
                onOpen={() => go({ tab: "invoices" })}
              />
            )}

            {hasTodoModule && (
              <section aria-labelledby="overview-todo">
                <div className="mb-3 flex items-center gap-2.5">
                  <h2 id="overview-todo" className="text-lg font-bold tracking-tight">
                    {t("portal.overview.todoTitle")}
                  </h2>
                  {attention.length > 0 && <Badge className="tabular-nums">{attention.length}</Badge>}
                </div>
                {loading ? (
                  <Card className="overflow-hidden">
                    <ListSkeleton rows={3} label={t("common.loading")} />
                  </Card>
                ) : unsure ? (
                  <Card>
                    <EmptyState
                      icon={AlertCircle}
                      title={t("portal.loadFailed")}
                      action={
                        <Button variant="outline" onClick={() => setAttempt((n) => n + 1)}>
                          <RefreshCw />
                          {t("portal.retry")}
                        </Button>
                      }
                    />
                  </Card>
                ) : attention.length === 0 ? (
                  <Card>
                    <EmptyState
                      icon={CheckCircle2}
                      title={t("portal.overview.clearTitle")}
                      body={t("portal.overview.clearBody")}
                    />
                  </Card>
                ) : (
                  <AttentionList items={attention} payable={payable} payBusyId={busyId} onPay={pay} onOpen={open} />
                )}
                {payFailed && (
                  <p role="alert" className="mt-3 text-sm text-destructive">{t("portal.invoices.payFailed")}</p>
                )}
              </section>
            )}

            {activeProjects.length > 0 && (
              <ActiveProjects
                projects={activeProjects.slice(0, PREVIEW_ROWS)}
                onOpen={(id) => go({ tab: "projects", project: id })}
                onAll={() => go({ tab: "projects" })}
              />
            )}
          </div>

          <aside className="min-w-0 space-y-4">
            {shows("invoices") && summary && (
              <div className="max-lg:hidden">
                <BalanceCard
                  outstandingCents={summary.stats.outstandingCents}
                  totals={totals}
                  actionLabel={t(shows("statement") ? "portal.overview.openStatement" : "portal.overview.openInvoices")}
                  onAction={() => go({ tab: shows("statement") ? "statement" : "invoices" })}
                />
              </div>
            )}
            {shows("support") && lists && (
              <SupportCard
                tickets={tickets}
                onOpen={(id) => go({ tab: "support", ticket: id })}
                onAll={() => go({ tab: "support" })}
                onNew={() => go({ tab: "support", new: "1" })}
              />
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
